/* eslint-disable react/prop-types */
import '@progress/kendo-theme-default/dist/all.css';

import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { getter } from '@progress/kendo-react-common';
import {
  getSelectedState,
  GridColumn,
  GridNoRecords,
} from '@progress/kendo-react-grid';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TrlNoDataFeedback } from '»components/atoms';
import { iff } from '»utils/code';

import { TrlButtonCell } from './TrlCells/TrlButtonCell/TrlButtonCell';
import { TrlFooterCell } from './TrlFooter';
import {
  ArrowStyled,
  GridStyled,
  MultiHeaderColStyled,
  NormalTableCell,
  NormalTableRow,
  TdExpandedStyled,
} from './TrlGrid.styles';

const SELECTED_FIELD = 'selected';
const ADJUST_PADDING = 4;
const COLUMN_MIN = 4;
const DUMMY_END_COLUMNS = [
  {
    title: ' ',
    field: 'dummy',
    width: 5,
    locked: true,
  },
];

const NormalHeaderCol = (props) => (
  <th {...props.thProps}>{props.headerComponent ?? props.title}</th>
);
const MultiHeaderCol = (props) => (
  <MultiHeaderColStyled {...props.thProps}>
    {props.headerComponent ?? ' '}
  </MultiHeaderColStyled>
);

const NormalCell = (props) => (
  <NormalTableCell
    {...props}
    title={
      props.dataItem[props.field] ? props.dataItem[props.field] : undefined
    }
  >
    {props.dataItem[props.field]}
  </NormalTableCell>
);

export const TrlGrid = (gridProps) => {
  const {
    id,
    columns,
    data,
    expandChange,
    selectedItem,
    selectedItemChange,
    DetailComponent,
    dataItemKey,
    extraHeight,
    isFooterDetails,
    maxHeight,
    height,
    open,
    virtualScrollingProps,
    gridWidth,
    noBorders,
    hasTransparentBackground,
    noMarginTop,
    remainingGridHeight,
    otherGridColor,
    otherScrollColor,
    otherFirefoxSecScrollColor,
    hasHorizontalScroll,
    hasVerticalScroll,
    noDummyEndColumn,
  } = gridProps;

  const [ColumnsWrapper, setColumnsWrapper] = useState(
    noDummyEndColumn ? columns : columns.concat(DUMMY_END_COLUMNS),
  );
  const [selectedState, setSelectedState] = useState({ 1: true });
  const [expandedElement, setExpandedElement] = useState(null);
  const [skip, setSkip] = useState(0);

  const isFirefox = navigator.userAgent.includes('Firefox');

  const PAGE_SIZE = !isFirefox && virtualScrollingProps?.pageSize;
  const ROW_HEIGHT = !isFirefox && virtualScrollingProps?.rowHeight;
  const SCROLLABLE = isFirefox
    ? 'scrollable'
    : (virtualScrollingProps?.scrollable ?? 'scrollable');

  const idGetter = getter(dataItemKey);

  const pageChange = (event) => {
    setSkip(event.page.skip);
  };

  const customCell = (props) => {
    const { tdProps } = props;

    if (DetailComponent !== undefined)
      return (
        <TdExpandedStyled
          onClick={() => {
            tdProps.onClick();
            expandChange(props);
            setExpandedElement({
              [props.dataItem.symbol]: !props.dataItem.expanded,
            });
          }}
          className={`k-grid-content-sticky ${props.className}`}
        >
          <ArrowStyled icon={props.expanded ? faChevronUp : faChevronDown} />
        </TdExpandedStyled>
      );

    return (
      <td {...props} colSpan={props.colSpan}>
        {props.children}
      </td>
    );
  };

  const customRow = (trElement, onRowClick) => {
    const { className } = trElement.trProps;
    const isLastDataRow = trElement.dataIndex === data.length - 1;
    const shouldRenderFillerRow = remainingGridHeight > 0 && isLastDataRow;
    const fillerColSpan = trElement.children?.length || 1;

    const fillerRow = shouldRenderFillerRow && (
      <tr className="k-table-row trl-grid-filler-row" aria-hidden="true">
        <td
          className="k-table-td trl-grid-filler-cell"
          colSpan={fillerColSpan}
        />
      </tr>
    );

    if (Object.keys(trElement.dataItem).length > 2)
      return (
        <>
          <NormalTableRow
            className={className}
            {...trElement.trProps}
            onClick={() => {
              onRowClick(trElement);
            }}
          >
            {trElement.children}
          </NormalTableRow>
          {trElement.dataItem.expanded && (
            <tr
              className="k-table-row k-detail-row"
              role="row"
              aria-rowindex="4"
            >
              <td
                className="k-table-td k-hierarchy-cell"
                role="gridcell"
                aria-label="Grid empty cell"
              />
              <td
                className="k-table-td k-detail-cell"
                role="gridcell"
                colSpan={trElement.children.length - 1}
                aria-colindex="2"
              >
                <DetailComponent dataItem={trElement.dataItem} />
              </td>
            </tr>
          )}
          {fillerRow}
        </>
      );

    return (
      <>
        <tr className={className}>
          {[...trElement.children].map((childData) => (
            <td
              key={`td${childData.key}`}
              aria-label={`Cell ${childData.key}`}
            />
          ))}
        </tr>
        {fillerRow}
      </>
    );
  };

  const rowRender = (trElement) => customRow({ ...trElement }, onRowClick);

  const minGridWidth = useRef(0);
  const grid = useRef(null);
  const [applyMinWidth, setApplyMinWidth] = useState(false);
  const [gridCurrent, setGridCurrent] = useState(0);

  useEffect(() => {
    grid.current = document.querySelector('.k-grid');
    window.addEventListener('resize', handleResize);
    ColumnsWrapper.map((item) =>
      item.minWidth !== undefined
        ? (minGridWidth.current += item.minWidth)
        : minGridWidth.current,
    );
    setGridCurrent(grid.current.offsetWidth);
    setApplyMinWidth(grid.current.offsetWidth < minGridWidth.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!noDummyEndColumn) setColumnsWrapper(columns.concat(DUMMY_END_COLUMNS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  useEffect(() => {
    if (selectedItem && Object.keys(selectedItem).length > 0) {
      const obj = `{"${selectedItem[dataItemKey]}": true}`;
      const newSelectedState = JSON.parse(obj);

      if (!selectedState[selectedItem[dataItemKey]])
        setSelectedState(newSelectedState);
    }
  }, [selectedItem]);

  const handleResize = () => {
    if (grid.current.offsetWidth < minGridWidth && !applyMinWidth) {
      setApplyMinWidth(true);
    } else if (grid.current.offsetWidth > minGridWidth) {
      setGridCurrent(grid.current.offsetWidth);
      setApplyMinWidth(false);
    }
  };

  const setWidth = (minWidth) => {
    // eslint-disable-next-line no-param-reassign
    minWidth === undefined ? (minWidth = 0) : null;
    let width = applyMinWidth
      ? minWidth
      : minWidth + (gridCurrent - minGridWidth.current) / ColumnsWrapper.length;

    width < COLUMN_MIN ? width : (width -= ADJUST_PADDING);
    return width;
  };

  const gridCellComponents = {
    TrlButtonCell,
    TrlFooterCell,
  };

  const CustomCell = (props) => {
    const col = ColumnsWrapper.find((column) => {
      if (column.field === props.field) return column;
      return column.children?.some((item) => {
        return item.field === props.field;
      });
    });

    return gridCellComponents[
      col.cellTemplate ??
        col.children.find((c) => c.field === props.field)?.cellTemplate
    ]({
      ...props,
      column: col.cellTemplate
        ? col
        : col.children.find((c) => c.field === props.field),
    });
  };

  const CustomCellFooter = (props) => {
    const col = ColumnsWrapper.find((column) => {
      if (column.field === props.field) return column;
      return column.children?.some((item) => {
        return item.field === props.field;
      });
    });

    return gridCellComponents[
      col.footerCellTemplate ??
        col.children.find((c) => c.field === props.field)?.footerCellTemplate
    ]({
      ...props,
      data,
      column: col.cellTemplate
        ? col
        : col.children.find((c) => c.field === props.field),
    });
  };

  const selectionChange = useCallback(
    (event) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState,
        dataItemKey,
      });
      setSelectedState(newSelectedState);
      if (selectedItemChange !== null) selectedItemChange(newSelectedState);
    },
    [dataItemKey, selectedItemChange, selectedState],
  );

  const onRowClick = (e) => {
    const searchItem = data.find(
      (item) => item[dataItemKey] === e.dataItem[dataItemKey],
    );
    const obj = `{"${searchItem[dataItemKey]}": true}`;
    const newSelectedState = JSON.parse(obj);
    setSelectedState(newSelectedState);
    if (selectedItemChange !== undefined) selectedItemChange(e.dataItem);
  };

  return (
    <GridStyled
      sortable={{ allowUnsort: true, mode: 'multiple' }}
      data={data?.map((item) => ({
        ...item,
        [SELECTED_FIELD]: selectedState[idGetter(item)],
      }))}
      open={open}
      height={height}
      maxHeight={
        maxHeight ||
        (grid.current
          ? `${
              window.innerHeight - grid.current.offsetTop - (extraHeight || 0)
            }px`
          : 'unset')
      }
      rowHeight={ROW_HEIGHT}
      pageSize={PAGE_SIZE}
      total={data.length}
      skip={skip}
      scrollable={SCROLLABLE}
      onPageChange={pageChange}
      detailExpand={expandedElement}
      onDetailExpandChange={(event) => {
        setExpandedElement(event.detailExpand);
        if (expandChange) expandChange(event);
      }}
      rows={{ data: rowRender }}
      hasDetail={DetailComponent !== undefined}
      selectable={{ enabled: true, mode: 'single' }}
      onSelectionChange={selectionChange}
      selectedItemChange={selectedItemChange || null}
      select={selectedState}
      dataItemKey={dataItemKey}
      onRowClick={onRowClick}
      id={id}
      isFooterDetails={isFooterDetails}
      gridWidth={gridWidth}
      noBorders={noBorders}
      hasTransparentBackground={hasTransparentBackground}
      noMarginTop={noMarginTop}
      remainingGridHeight={remainingGridHeight}
      gridColor={otherGridColor}
      scrollColor={otherScrollColor}
      firefoxSecScrollColor={otherFirefoxSecScrollColor}
      hasHorizontalScroll={hasHorizontalScroll}
      hasVerticalScroll={hasVerticalScroll}
    >
      <GridNoRecords>
        <TrlNoDataFeedback
          margin="0 auto"
          height={`${remainingGridHeight > 0 ? remainingGridHeight : 160}px`}
        />
      </GridNoRecords>
      {DetailComponent !== undefined && (
        <GridColumn
          field="expanded"
          title=" "
          width="50px"
          cells={{ data: customCell }}
          editable={false}
        />
      )}
      {ColumnsWrapper.map((column, index) => {
        return (
          <GridColumn
            field={column.field}
            title={typeof column.title === 'string' ? column.title : ' '}
            cells={{
              headerCell: (props) =>
                column.children !== undefined ? (
                  <MultiHeaderCol
                    {...props}
                    headerComponent={column.headerComponent}
                  />
                ) : (
                  <NormalHeaderCol
                    {...props}
                    headerComponent={column.headerComponent}
                  />
                ),
              data: column.cellTemplate !== undefined ? CustomCell : NormalCell,
              footerCell:
                column.footerCellTemplate !== undefined && CustomCellFooter,
            }}
            // eslint-disable-next-line react/no-children-prop
            children={column.children !== undefined ? column.children : null}
            headerClassName={
              column.children !== undefined
                ? 'multiHeader'
                : iff(
                    column.headerClassName !== undefined,
                    column.headerClassName,
                    null,
                  )
            }
            className={column.className !== undefined ? column.className : null}
            locked={column.locked ? column.locked : null}
            reorderable={column.reorderable ? column.reorderable : null}
            filterable={column.filterable ? column.filterable : null}
            sortable={column.sortable ? column.sortable : null}
            visible={column.visible ? column.visible : null}
            resizable={column.resizable ? column.resizable : null}
            width={column.minWidth ? setWidth(column.minWidth) : column.width}
            key={index}
          >
            {column.children &&
              column.children.map((column1) => (
                <GridColumn
                  key={column1.field}
                  field={column1.field}
                  title={column1.title ?? ' '}
                  cells={{
                    headerCell: (props) =>
                      column1.children !== undefined ? (
                        <MultiHeaderCol
                          {...props}
                          headerComponent={column.headerComponent}
                        />
                      ) : (
                        <NormalHeaderCol
                          {...props}
                          headerComponent={column.headerComponent}
                        />
                      ),
                    data:
                      column1.cellTemplate !== undefined
                        ? CustomCell
                        : NormalCell,
                    footerCell:
                      column1.footerCellTemplate !== undefined &&
                      CustomCellFooter,
                  }}
                  headerClassName={
                    column1.children !== undefined
                      ? 'multiHeader'
                      : iff(
                          column1.headerClassName !== undefined,
                          column1.headerClassName,
                          null,
                        )
                  }
                  className={
                    column1.className !== undefined ? column1.className : null
                  }
                  filterable={column1.filterable ? column1.filterable : null}
                  sortable={column1.sortable ? column1.sortable : null}
                  visible={column1.visible ? column1.visible : null}
                  resizable={column1.resizable ? column1.resizable : null}
                  width={
                    column1.minWidth
                      ? setWidth(column1.minWidth)
                      : column1.width
                  }
                />
              ))}
          </GridColumn>
        );
      })}
    </GridStyled>
  );
};
